/**
 * Rôles et aiguillage.
 *
 * Ce module ne dépend ni de la base ni du serveur : il est importable depuis le
 * proxy comme depuis un composant, et c'est le seul endroit où la règle
 * d'aiguillage du handoff est écrite.
 *
 * La règle, mot pour mot : « e-mail se terminant par `@umiros.com` → espace
 * interne. **Le rôle précis est porté par le compte, pas déduit de l'adresse.** »
 *
 * Le domaine tranche donc une seule question — interne ou apprenant — et rien de
 * plus. Déduire « commercial » d'une adresse serait une élévation de privilège à
 * portée de quiconque contrôle une boîte mail.
 *
 * La maquette de connexion montre un aiguillage en direct pendant la saisie :
 * c'est une démonstration du comportement attendu, pas une autorisation à
 * décider côté client.
 */

export const ROLES = ['apprenant', 'administratif', 'commercial', 'formateur', 'pilotage'] as const
export type Role = (typeof ROLES)[number]

export const ROLES_INTERNES = ['administratif', 'commercial', 'formateur', 'pilotage'] as const
export type RoleInterne = (typeof ROLES_INTERNES)[number]

const DOMAINE_INTERNE = '@umiros.com'

/** Racine de l'espace monté pour chaque rôle. */
export const ESPACE: Record<Role, string> = {
  apprenant: '/espace',
  administratif: '/admin',
  commercial: '/commercial',
  formateur: '/formateur',
  pilotage: '/pilotage',
}

/** Libellé affiché sur l'écran d'attribution du rôle. */
export const LIBELLE_ESPACE: Record<Role, string> = {
  apprenant: 'Mon espace',
  administratif: 'Espace administratif',
  commercial: 'Espace commercial',
  formateur: 'Espace formateur',
  pilotage: 'Espace pilotage',
}

export function estRoleInterne(role: Role): role is RoleInterne {
  return (ROLES_INTERNES as readonly string[]).includes(role)
}

/**
 * Une adresse ouvre-t-elle droit à un compte interne ?
 *
 * Ne dit **pas** quel rôle : seulement si le compte peut en porter un autre
 * qu'`apprenant`. Sert à la création de compte et comme garde-fou à la
 * connexion, jamais à choisir un rôle.
 */
export function adresseInterne(email: string): boolean {
  return email.trim().toLowerCase().endsWith(DOMAINE_INTERNE)
}

/**
 * Cohérence entre l'adresse et le rôle stocké.
 *
 * Un compte interne dont l'adresse a changé de domaine, ou un apprenant à qui un
 * rôle interne aurait été attribué par erreur, doit être arrêté plutôt que
 * connecté sur le mauvais espace.
 */
export function roleCoherent(email: string, role: Role): boolean {
  return estRoleInterne(role) ? adresseInterne(email) : true
}

/** Chemin de destination après connexion. */
export function espaceDe(role: Role): string {
  return ESPACE[role]
}

/** À quel rôle appartient un chemin ? Sert au proxy pour refuser les hors-espace. */
export function roleDuChemin(chemin: string): Role | null {
  const entree = Object.entries(ESPACE).find(
    ([, racine]) => chemin === racine || chemin.startsWith(`${racine}/`),
  )
  return (entree?.[0] as Role) ?? null
}
